import {
  InputAdornment,
  Stack,
  Button,
  TextField,
  useTheme,
  Autocomplete,
  Checkbox,
  Tooltip,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Iconify from "../../../components/iconify";
import { statusOptionsProps, TillOrderStatus } from "../../../@types/till";
import ExportIcon from "../../../assets/icons/ExportIcon";
import { useState } from "react";
import { useSnackbar } from "notistack";
import { TillService } from "../../../api/till.service";
import { saveAs } from "file-saver";
import { useAuthContext } from "../../../auth/useAuthContext";
import { MockDataService } from "../../../services/mockData.service";

type Props = {
  searchValue: string;
  statusValues: statusOptionsProps[];
  onResetFilter: Function;
  onSearchFilter: Function;
  onStatusFilter: Function;
};

const statusOption: statusOptionsProps[] = [
  {
    label:
      TillOrderStatus.PENDING.charAt(0).toUpperCase() +
      TillOrderStatus.PENDING.slice(1),
    value: TillOrderStatus.PENDING,
  },
  {
    label:
      TillOrderStatus.PARTIAL.charAt(0).toUpperCase() +
      TillOrderStatus.PARTIAL.slice(1),
    value: TillOrderStatus.PARTIAL,
  },
  {
    label:
      TillOrderStatus.PAID.charAt(0).toUpperCase() +
      TillOrderStatus.PAID.slice(1),
    value: TillOrderStatus.PAID,
  },
];

export default function TillTableToolbar({
  searchValue,
  statusValues,
  onResetFilter,
  onSearchFilter,
  onStatusFilter,
}: Props) {
  const theme = useTheme();
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  const handleChange = (_e: any, options: any) => {
    onStatusFilter(options);
  };

  const exportList = async () => {
    try {
      setExportLoading(true);

      // For patient users, create mock CSV data
      if (user?.roleId === "patient") {
        const mockData = MockDataService.getMockTillData(user);
        const csvContent =
          "ID,Patient Name,Postcode,Phone,Email,Status,Pickup Date,Pickup Time\n" +
          mockData
            .map((item) => {
              const pickupDate =
                item.pickupDate instanceof Date
                  ? item.pickupDate.toISOString().slice(0, 10)
                  : "";
              return `${item.id},${item.name},${item.postCode},${item.phone},${
                item.email
              },${item.status || ""},${pickupDate},${item.pickupTime || ""}`;
            })
            .join("\n");

        const blobFile = new Blob([csvContent], {
          type: "text/csv;charset=utf-8",
        });
        saveAs(blobFile, `TillRecords.csv`);
        setExportLoading(false);
        enqueueSnackbar("Export successful!", {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
        });
        return;
      }

      const tillService = new TillService();
      const response = await tillService.exportTills();
      setExportLoading(false);

      const blobFile = new Blob([response.data], {
        type: "text/csv;charset=utf-8",
      });
      saveAs(blobFile, `TillRecords.csv`);
      enqueueSnackbar("Export successful!", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
      });
    } catch (error: any) {
      setExportLoading(false);
      enqueueSnackbar(error?.message || "Something went wrong!", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
      });
    }
  };

  return (
    <Stack
      spacing={2}
      alignItems="center"
      direction={{
        xs: "column",
        md: "row",
      }}
      sx={{ px: 2.5, py: 3 }}>
      <TextField
        fullWidth
        value={searchValue}
        onChange={(e) => onSearchFilter(e.target.value)}
        placeholder="Search Till By Patient Name, Phone or Email"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: "text.disabled" }} />
            </InputAdornment>
          ),
        }}
      />
      <Autocomplete
        multiple
        id="statusOption"
        options={statusOption}
        limitTags={1}
        disableCloseOnSelect
        onChange={handleChange}
        sx={{
          width: 234,
          overflow: "hidden",
          minWidth: 234,
          // height: 40,
          // borderRadius: "10px",
          backgroundColor: theme.palette.background.paper,
          "& .MuiAutocomplete-inputRoot": {
            flexWrap: "nowrap !important",
          },
          // "& .MuiOutlinedInput-root": {
          //   height: 40,
          // },
          // "& .MuiOutlinedInput-root fieldset": {
          //   border: "none",
          // },
          "& .MuiAutocomplete-endAdornment": {
            right: 13,
            // top: "calc(50% - 12px)",
          },
          // "& .MuiIconButton-root": {
          //   "& svg": {
          //     fill: theme.palette.mode !== "dark" ? "#000000" : "#e9f2f2",
          //   },
          // },
        }}
        value={statusValues}
        getOptionLabel={(option) => option.label}
        renderTags={(value, getTagProps) => (
          <Chip
            {...getTagProps({ index: 0 })}
            size="small"
            label={
              value.length === 0
                ? "Order Status"
                : value.length === 1
                ? value[0].label
                : `${value.length} selected`
            }
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              style={{ marginRight: 4 }}
              checked={selected}
            />
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} placeholder="Order Status" />
        )}
      />
      {exportLoading ? (
        <IconButton
          color="default"
          sx={{
            p: 0,
            backgroundColor: "white",
            width: "40px",
            height: "40px",
            borderRadius: "15px",
          }}>
          <CircularProgress size={24} color="inherit" />
        </IconButton>
      ) : (
        <Tooltip title="Export List">
          <IconButton color="default" onClick={exportList} sx={{ p: 0 }}>
            <ExportIcon />
          </IconButton>
        </Tooltip>
      )}

      {searchValue || statusValues.length > 0 ? (
        <Button color="error" onClick={() => onResetFilter()}>
          Cancel
        </Button>
      ) : null}
    </Stack>
  );
}
